import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CourseViewer } from '../CourseViewer';
import { getCourseById, getUserProfile, getOrganization, getStudyById, getOrgStudies, getCourseStudies, logout } from '../../services/firebase';
import { Course, Study, UserProfile, Organization } from '../../types';
import { Icons } from '../ui/Icons';

export const CourseViewerRoute: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const [course, setCourse] = useState<Course | null>(null);
  const [studies, setStudies] = useState<Study[]>([]);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      if (!courseId) {
        setError('No course ID provided');
        setLoading(false);
        return;
      }

      try {
        // Load course first
        const courseData = await getCourseById(courseId);
        if (!courseData) {
          setError('Course not found');
          setLoading(false);
          return;
        }
        setCourse(courseData);

        // Load course studies
        const courseStudies = await getCourseStudies(courseData.studyIds || []);
        setStudies(courseStudies);

        // Try to load user profile if authenticated
        const userId = localStorage.getItem('userId');
        if (userId) {
          const userProfile = await getUserProfile(userId);
          if (userProfile) {
            setUser(userProfile);
            if (userProfile.orgId) {
              const org = await getOrganization(userProfile.orgId);
              setOrganization(org);
            }
          }
        }

        setLoading(false);
      } catch (err) {
        console.error('Error loading course:', err);
        setError('Failed to load course');
        setLoading(false);
      }
    };

    loadData();
  }, [courseId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Icons.Loader className="w-8 h-8 text-primary animate-spin mx-auto mb-2" />
          <p className="text-gray-600">Loading course...</p>
        </div>
      </div>
    );
  }

  if (error || !course) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Icons.X className="w-12 h-12 text-red-500 mx-auto mb-2" />
          <h2 className="text-xl font-bold text-gray-800 mb-2">Error</h2>
          <p className="text-gray-600 mb-4">{error || 'Course not found'}</p>
          <button
            onClick={() => navigate(user ? '/dashboard' : '/gallery')}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-900"
          >
            {user ? 'Go to Dashboard' : 'Browse Public Content'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <CourseViewer
      courseId={course.id}
      studies={studies}
      user={user}
      organization={organization}
      onLogout={user ? logout : undefined}
      onClose={() => navigate(user ? '/dashboard' : '/gallery')}
      onStartStudy={(studyId) => {
        navigate(`/study/${studyId}?from=course&courseId=${courseId}`);
      }}
    />
  );
};
